import { useId, useState, type FormEvent } from "react";
import { Megaphone } from "lucide-react";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { Modal } from "../Modal";
import type { Announcement } from "../../services/communicationService";
import { todayIso } from "../../utils/dates";
import { ANNOUNCEMENT_TYPES, AUDIENCES, PRIORITIES } from "./communicationRegisters";

export interface AnnouncementDraft {
  title: string;
  content: string;
  target_audience: Announcement["target_audience"];
  priority: Announcement["priority"];
  announcement_type: Announcement["announcement_type"];
  expires_at: string | null;
  show_on_sign_in: boolean;
}

/** An announcement to the school: who it is for, how pressing, and the text. */
export function AnnouncementComposer({
  pending,
  error,
  onPublish,
  onClose,
}: {
  pending?: boolean;
  error?: string;
  onPublish: (draft: AnnouncementDraft) => void;
  onClose: () => void;
}) {
  const formId = useId();
  const [draft, setDraft] = useState<AnnouncementDraft>({
    title: "",
    content: "",
    target_audience: "all",
    priority: "normal",
    announcement_type: "general",
    expires_at: null,
    show_on_sign_in: false,
  });
  const set = (changes: Partial<AnnouncementDraft>) => setDraft((current) => ({ ...current, ...changes }));
  const ready = draft.title.trim().length > 0 && draft.content.trim().length > 0;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (ready) onPublish({ ...draft, title: draft.title.trim(), content: draft.content.trim() });
  };

  return (
    <Modal
      isOpen
      size="3xl"
      title="New announcement"
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto text-xs text-gray-500">
            Published straight away, and everyone in the audience is notified.
          </span>
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" form={formId} disabled={!ready || pending} className="btn btn-primary">
            <Megaphone className="mr-2 h-4 w-4" />
            {pending ? "Publishing…" : "Publish"}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-5">
        {error && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <FormSection title="Who it is for" divided={false} columns={4}>
          <Field label="Audience">
            <select value={draft.target_audience} onChange={(event) => set({ target_audience: event.target.value as AnnouncementDraft["target_audience"] })} className="input mt-1">
              {Object.entries(AUDIENCES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </Field>
          <Field label="Kind">
            <select value={draft.announcement_type} onChange={(event) => set({ announcement_type: event.target.value as AnnouncementDraft["announcement_type"] })} className="input mt-1">
              {Object.entries(ANNOUNCEMENT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={draft.priority} onChange={(event) => set({ priority: event.target.value as AnnouncementDraft["priority"] })} className="input mt-1">
              {Object.entries(PRIORITIES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </Field>
          <Field label="Show until" optional hint="Left empty, it stays up.">
            <input type="date" min={todayIso()} value={draft.expires_at ?? ""} onChange={(event) => set({ expires_at: event.target.value || null })} className="input mt-1" />
          </Field>
        </FormSection>

        <FormSection title="What it says" columns={1}>
          <Field label="Title">
            <input required maxLength={200} value={draft.title} onChange={(event) => set({ title: event.target.value })} className="input mt-1" placeholder="e.g. Visiting day moved to Saturday" />
          </Field>
          <Field as="div" label="Announcement">
            <AIAssistField
              name="content"
              rows={8}
              value={draft.content}
              onChange={(next) => set({ content: next })}
              placeholder="Write the announcement"
              context={{
                key: "communication.announcement",
                params: { title: draft.title, audience: draft.target_audience, priority: draft.priority, announcement_type: draft.announcement_type, expires_at: draft.expires_at },
              }}
              draftLabel="Draft the announcement"
              draftHint="Reads the title and audience. Jot the facts first (dates, places, amounts) and it writes them up; anything missing is left in [brackets]."
            />
          </Field>
          <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={draft.show_on_sign_in}
              onChange={(event) => set({ show_on_sign_in: event.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
            />
            <span>
              <span className="font-medium text-gray-900">Also show it on the sign-in page</span>
              <span className="block text-xs text-gray-500">
                Anyone can see the sign-in page, without an account, so tick this only for news meant for everyone. It comes off when the announcement expires.
              </span>
            </span>
          </label>
        </FormSection>
      </form>
    </Modal>
  );
}
